import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { UserCircle, X, Award, ChevronRight } from 'lucide-react';
import { Badge } from './ui/badge';

const DISMISS_KEY = 'profile_nudge_dismissed';
const DISMISS_DAYS = 30;
// Shown at most once per browser session, and never on /perfil, which has its own completeness card
const SESSION_KEY = 'profile_nudge_shown';

export function ProfileNudge({ user, onOpenProfile }) {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();
  const onProfile = pathname.startsWith('/perfil');

  // Leaving the page it appeared on (or opening Perfil) closes it for the rest of the session
  useEffect(() => { setVisible(false); }, [pathname]);

  useEffect(() => {
    if (onProfile || !user?.profile_completeness) return;
    const { complete, missing } = user.profile_completeness;
    if (complete) return; // Profile is complete, no nudge needed

    // Check if dismissed recently
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const dismissedAt = new Date(dismissed);
        const daysSince = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince < DISMISS_DAYS) return;
      }
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {}

    // Only show if there are missing fields
    if (missing?.length > 0) {
      // Delay to not overlap with page load
      const timer = setTimeout(() => {
        setVisible(true);
        try { sessionStorage.setItem(SESSION_KEY, '1'); } catch {}
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user, onProfile]);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch {}
  };

  if (!visible || onProfile || !user?.profile_completeness) return null;

  const { score, missing } = user.profile_completeness;

  const fieldLabels = {
    nombre: 'nombre completo',
    barrio: 'barrio',
  };

  const missingText = missing
    .filter(f => fieldLabels[f])
    .map(f => fieldLabels[f])
    .join(' y ');

  return (
    <div className="fixed bottom-28 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-24 sm:max-w-sm z-[900] animate-fade-in-up" data-testid="profile-nudge">
      <div className="card-solid p-4 border-2 border-torre-teal/20 shadow-lg rounded-2xl bg-white">
        <button onClick={dismiss} aria-label="Cerrar aviso" className="absolute top-2 right-2 p-1 rounded-full hover:bg-slate-100 text-slate-500" data-testid="nudge-dismiss">
          <X size={14} />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-torre-teal/10 flex items-center justify-center shrink-0">
            <UserCircle size={20} className="text-torre-teal" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-torre-slate mb-1">Completa tu perfil</p>
            <p className="text-xs text-slate-500 mb-2">
              {missingText ? `Añade tu ${missingText} para mejor experiencia` : 'Tu perfil está casi completo'}
            </p>

            {/* Progress bar */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-torre-teal rounded-full transition-all" style={{ width: `${score}%` }} />
              </div>
              <span className="text-[11px] font-bold text-torre-teal">{score}%</span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => { dismiss(); onOpenProfile?.(); }}
                className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 px-3 py-2 bg-torre-teal text-white text-xs font-bold rounded-xl hover:bg-torre-teal/90 transition-colors"
                data-testid="nudge-complete-btn">
                Completar
                <ChevronRight size={12} />
              </button>
              <Badge className="text-[11px] font-bold bg-amber-50 text-amber-700 border-amber-200 rounded-full flex items-center gap-1 shrink-0">
                <Award size={9} /> +5 karma
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
